/**
 * CORE Living Core V2 renderer specification.
 *
 * Source: packages/CORE-Living-Core-V2/source/build_core_v2.py and
 * core-v2-shaders.json. The only adaptation is `sourceUV` / `displayFromSource`:
 * the approved 16:9 source mask is cover-mapped, rather than stretched, for
 * portrait and non-16:9 login viewports. The original orbital equations,
 * timings, 16 ribbons and 360 particles are kept verbatim.
 */

export const CORE_LIVING_CORE_V2 = {
  cycleSeconds: 12,
  ribbonCount: 16,
  particleCount: 360,
  sourceAspect: 16 / 9,
  sourcePath: "/core/living-core-v2/core-sphere-source-mask.webp",
  maxDevicePixels: 4_000_000,
  maxDevicePixelRatio: 2,
} as const;

export type CoreV2Effect = {
  readonly vertex: string;
  readonly fragment: string;
  readonly data: Float32Array;
  readonly attribute: "aData";
  readonly components: 4;
  readonly mode: "triangles" | "points";
};

export const CORE_V2_VERTEX = `
attribute vec2 aPosition;
void main(){gl_Position=vec4(aPosition,0.0,1.0);}`;

// This is the approved V2 base shader with the source texture sampled through
// aspect-preserving cover UVs. The alpha mask is what removes raster CORE/CTA.
export const CORE_V2_BASE_FRAGMENT = `
precision highp float;
uniform sampler2D uImage;
uniform vec2 uResolution;
uniform vec2 uMouse;
uniform float uTime;
uniform float uImpulse;
const float PI=3.14159265359;
const float SOURCE_ASPECT=1.77777778;
vec2 sourceUV(vec2 uv){
 float aspect=uResolution.x/max(uResolution.y,1.0);
 if(aspect<SOURCE_ASPECT){return vec2(.5+(uv.x-.5)*aspect/SOURCE_ASPECT,uv.y);}
 return vec2(uv.x,.5+(uv.y-.5)*SOURCE_ASPECT/aspect);
}
void main(){
 vec2 uv=vec2(gl_FragCoord.x/uResolution.x,1.0-gl_FragCoord.y/uResolution.y);
 vec2 source=sourceUV(uv);
 float phase=uTime*2.0*PI/12.0;
 vec2 original=vec2(.5,.297);
 vec2 drift=vec2(.005*sin(phase),.006*sin(phase*2.0));
 vec2 center=original+drift+uMouse*vec2(.010,.013);
 vec2 asp=vec2(SOURCE_ASPECT,1.0);
 vec2 p=(source-center)*asp;
 float r=length(p);
 float region=1.0-smoothstep(.235,.291,r);
 float breath=1.0+.030*sin(phase*3.0)+.014*sin(phase*2.0)+uImpulse*.022;
 float angle=.22*sin(phase)+.09*sin(phase*2.0)+.19*sin(phase*2.0+r*11.0)*exp(-r*3.0);
 mat2 rotate=mat2(cos(angle),-sin(angle),sin(angle),cos(angle));
 vec2 q=rotate*p/breath;
 float polar=atan(q.y,q.x);
 q+=normalize(q+vec2(.00001))*.0024*sin(polar*6.0-phase*3.0+r*34.0);
 vec2 movedUV=original+q/asp;
 vec4 approved=texture2D(uImage,mix(source,movedUV,region));
 vec3 col=mix(vec3(.01176,.01961,.03922),approved.rgb,approved.a);
 float energy=.22*sin(phase*3.0+polar*2.0-r*24.0)+.13*sin(phase*2.0);
 col*=1.0+region*energy;
 float nucleus=exp(-dot(p,p)*900.0);
 col+=vec3(.10,.23,.35)*nucleus*(.18+.14*sin(phase*3.0));
 float pulse=fract(uTime/4.0);
 float waveR=.045+.224*pulse;
 float wave=exp(-pow((r-waveR)*150.0,2.0))*sin(pulse*PI)*.037;
 col+=vec3(.35,.75,1.0)*wave*region;
 float sweep=pow(max(0.0,cos(polar-phase*2.0)),14.0)*exp(-pow((r-.224)*85.0,2.0));
 col+=vec3(.045,.12,.20)*sweep;
 col+=vec3(.08,.16,.24)*exp(-dot(p,p)*45.0)*uImpulse;
 gl_FragColor=vec4(col,1.0);
}`;

const TRANSFORM = `
const float PI=3.14159265359;
const float SOURCE_ASPECT=1.77777778;
vec3 rotX(vec3 p,float a){return vec3(p.x,p.y*cos(a)-p.z*sin(a),p.y*sin(a)+p.z*cos(a));}
vec3 rotY(vec3 p,float a){return vec3(p.x*cos(a)+p.z*sin(a),p.y,-p.x*sin(a)+p.z*cos(a));}
vec3 rotZ(vec3 p,float a){return vec3(p.x*cos(a)-p.y*sin(a),p.x*sin(a)+p.y*cos(a),p.z);}
vec2 displayFromSource(vec2 source){
 float aspect=uResolution.x/max(uResolution.y,1.0);
 if(aspect<SOURCE_ASPECT){return vec2(.5+(source.x-.5)*SOURCE_ASPECT/aspect,source.y);}
 return vec2(source.x,.5+(source.y-.5)*aspect/SOURCE_ASPECT);
}
vec2 screen(vec3 p,float phase){
 float b=1.0+.030*sin(phase*3.0)+.014*sin(phase*2.0)+uImpulse*.022;
 float perspective=1.0+p.z*.52;
 vec2 drift=vec2(.005*sin(phase),.006*sin(phase*2.0));
 vec2 source=vec2(.5,.297)+drift+uMouse*vec2(.010,.013)+p.xy*b*perspective/vec2(SOURCE_ASPECT,1.0);
 vec2 uv=displayFromSource(source);
 return vec2(uv.x*2.0-1.0,1.0-uv.y*2.0);
}
`;

export const CORE_V2_RIBBON_VERTEX = `
precision highp float;
attribute vec4 aData;
uniform vec2 uResolution;
uniform vec2 uMouse;
uniform float uTime;
uniform float uImpulse;
varying vec4 vColor;
${TRANSFORM}
vec3 orbit(float theta,float id,float phase){
 float radius=.165+.0047*id;
 vec3 p=vec3(cos(theta)*radius,sin(theta)*radius,0.0);
 p=rotX(p,.42+id*.213);
 p=rotY(p,id*1.071+phase*(mod(id,2.0)<.5?1.0:-1.0));
 return rotZ(p,.13*sin(phase*2.0)+id*.117);
}
void main(){
 float phase=uTime*2.0*PI/12.0;
 float id=aData.y;
 float direction=mod(id,2.0)<.5?1.0:-1.0;
 float speed=1.0+mod(id,3.0);
 float head=phase*speed*direction+id*2.31;
 float theta=head-aData.x*direction;
 vec3 p=orbit(theta,id,phase);
 vec2 pos=screen(p,phase);
 vec2 next=screen(orbit(theta+.004,id,phase),phase);
 vec2 tangent=normalize((next-pos)*uResolution);
 vec2 normal=vec2(-tangent.y,tangent.x);
 float fade=pow(max(0.0,1.0-aData.z),1.5);
 float depth=.38+.62*(p.z+.24)/.48;
 float thickness=(.7+1.5*fade)*uResolution.y/1440.0;
 pos+=normal*thickness*aData.w/uResolution;
 gl_Position=vec4(pos,0.0,1.0);
 vec3 cyan=vec3(.40,.88,1.0),violet=vec3(.70,.56,1.0);
 vec3 tint=mix(cyan,violet,mod(id,3.0)/2.0);
 vColor=vec4(tint,fade*depth*.66);
}`;

export const CORE_V2_RIBBON_FRAGMENT = `
precision highp float;
varying vec4 vColor;
void main(){gl_FragColor=vColor;}`;

export const CORE_V2_POINT_VERTEX = `
precision highp float;
attribute vec4 aData;
uniform vec2 uResolution;
uniform vec2 uMouse;
uniform float uTime;
uniform float uImpulse;
varying vec4 vColor;
${TRANSFORM}
void main(){
 float phase=uTime*2.0*PI/12.0;
 float id=aData.x,kind=aData.y;
 vec3 p;
 if(kind<.5){
  float n=id;
  float y=1.0-2.0*(n+.5)/360.0;
  float a=n*2.39996323;
  float radius=.155+.088*fract(n*.371);
  p=vec3(cos(a)*sqrt(1.0-y*y),y,sin(a)*sqrt(1.0-y*y))*radius;
  p=rotY(p,phase*(mod(n,2.0)<.5?1.0:-1.0));
  p=rotX(p,.15*sin(phase)+aData.z);
  float alpha=.24+.48*(.5+.5*sin(phase*3.0+id*1.27));
  alpha*=.32+.68*(p.z+.25)/.5;
  vColor=vec4(mix(vec3(.30,.76,1.0),vec3(.68,.50,1.0),fract(id*.123)),alpha);
  gl_PointSize=(2.5+4.3*fract(id*.217))*uResolution.y/1440.0;
 }else{
  float oid=id;
  float direction=mod(oid,2.0)<.5?1.0:-1.0;
  float theta=phase*(1.0+mod(oid,3.0))*direction+oid*2.31;
  float radius=.165+.0047*oid;
  p=vec3(cos(theta)*radius,sin(theta)*radius,0.0);
  p=rotX(p,.42+oid*.213);
  p=rotY(p,oid*1.071+phase*direction);
  p=rotZ(p,.13*sin(phase*2.0)+oid*.117);
  float alpha=.55+.40*(p.z+.24)/.48;
  vColor=vec4(mix(vec3(.38,.87,1.0),vec3(.76,.58,1.0),mod(oid,3.0)/2.0),alpha);
  gl_PointSize=(20.0+6.0*sin(phase*3.0+oid))*uResolution.y/1440.0;
 }
 gl_Position=vec4(screen(p,phase),0.0,1.0);
}`;

export const CORE_V2_POINT_FRAGMENT = `
precision highp float;
varying vec4 vColor;
void main(){
 vec2 p=gl_PointCoord-vec2(.5);
 float d=length(p);
 float halo=exp(-d*d*24.0)*.33;
 float hot=exp(-d*d*170.0);
 float alpha=(halo+hot*.8)*vColor.a;
 vec3 color=mix(vColor.rgb,vec3(.85,.96,1.0),hot);
 gl_FragColor=vec4(color,alpha);
}`;

function ribbonVertex(segment: number, side: -1 | 1, orbit: number) {
  const fraction = segment / 72;
  return [fraction * 1.42, orbit, fraction, side];
}

/** Exact loop shape used by build_core_v2.py (16 * 72 * 6 vertices). */
export function createCoreV2RibbonGeometry(): Float32Array {
  const values: number[] = [];
  for (let orbit = 0; orbit < CORE_LIVING_CORE_V2.ribbonCount; orbit += 1) {
    for (let segment = 0; segment < 72; segment += 1) {
      values.push(
        ...ribbonVertex(segment, -1, orbit),
        ...ribbonVertex(segment, 1, orbit),
        ...ribbonVertex(segment + 1, -1, orbit),
        ...ribbonVertex(segment + 1, -1, orbit),
        ...ribbonVertex(segment, 1, orbit),
        ...ribbonVertex(segment + 1, 1, orbit),
      );
    }
  }
  return new Float32Array(values);
}

/** Exact particle input used by build_core_v2.py: 360 globe particles + 16 heads. */
export function createCoreV2ParticleGeometry(): Float32Array {
  const values: number[] = [];
  for (let index = 0; index < CORE_LIVING_CORE_V2.particleCount; index += 1) {
    values.push(index, 0, Math.sin(index) * 0.18, 0);
  }
  for (let orbit = 0; orbit < CORE_LIVING_CORE_V2.ribbonCount; orbit += 1) {
    values.push(orbit, 1, 0, 0);
  }
  return new Float32Array(values);
}

export function createCoreV2Effects(): readonly CoreV2Effect[] {
  return [
    {
      vertex: CORE_V2_RIBBON_VERTEX,
      fragment: CORE_V2_RIBBON_FRAGMENT,
      data: createCoreV2RibbonGeometry(),
      attribute: "aData",
      components: 4,
      mode: "triangles",
    },
    {
      vertex: CORE_V2_POINT_VERTEX,
      fragment: CORE_V2_POINT_FRAGMENT,
      data: createCoreV2ParticleGeometry(),
      attribute: "aData",
      components: 4,
      mode: "points",
    },
  ];
}
